import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Images, Users, Boxes, MessageSquare } from "lucide-react";

// HIS SOP REPORT, 2026-10-08: *"how often parts were read with 5–10, 11–15, 16–20 and more than
// 20 photos — by person, by part type, and the reason they picked."* Admin-only, read-only, and
// it lives on the AI screen he already has — no new screen.
const PERIODS = [30, 90, 365];

export function PhotoUsageCard() {
  const [u, setU] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    api.get(`/ai/photo-usage?days=${days}`).then(({ data }) => setU(data)).catch(() => setU(false));
  }, [days]);

  if (u === false) return null;
  const buckets = u?.buckets || [];

  const Row = ({ label, counts, total }) => (
    <tr className="border-t border-slate-100">
      <td className="py-1.5 pr-2 text-slate-700 truncate max-w-[180px]">{label}</td>
      {buckets.map((b) => (
        <td key={b} className="py-1.5 px-1 text-center font-mono text-slate-600">{counts[b] || 0}</td>
      ))}
      <td className="py-1.5 pl-2 text-right font-mono font-bold text-slate-800">{total}</td>
    </tr>
  );

  return (
    <div data-testid="photo-usage-card" className="rounded-xl border border-slate-200 bg-white p-4 mt-4">
      <div className="flex items-center gap-2 mb-1">
        <Images className="w-5 h-5 text-violet-600" />
        <div className="font-bold">How many photos per read</div>
        <Badge className="bg-slate-100 text-slate-500 text-[10px] ml-1">admin · read-only</Badge>
        <div className="ml-auto flex gap-1">
          {PERIODS.map((p) => (
            <button key={p} type="button" data-testid={`photo-usage-days-${p}`} onClick={() => setDays(p)}
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${days === p ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
              {p}d
            </button>
          ))}
        </div>
      </div>
      <div className="text-xs text-slate-500 mb-3">
        {u ? <>Default is {u.default_photos} photos; more than that asks the person WHY, and more than {u.background_above} is read as a background job. {u.reads_total} read{u.reads_total === 1 ? "" : "s"} in the last {u.days} days{u.truncated ? ` (showing the most recent ${u.reads_scanned} of ${u.reads_total})` : ""}.</> : "Loading…"}
      </div>

      {!u ? (
        <div className="font-mono text-xs text-slate-400">LOADING…</div>
      ) : u.reads_total === 0 ? (
        <div data-testid="photo-usage-empty" className="text-xs text-slate-400">No photo reads in this period yet.</div>
      ) : (
        <>
          <table className="w-full text-[12px] mb-4">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                <th className="text-left pb-1">All reads</th>
                {buckets.map((b) => <th key={b} className="px-1 pb-1 text-center font-semibold">{b}</th>)}
                <th className="pb-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody data-testid="photo-usage-overall">
              <Row label="Photos per read" counts={u.overall} total={u.reads_scanned} />
            </tbody>
          </table>

          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
            <Users className="w-3 h-3" />By person
          </div>
          <table className="w-full text-[12px] mb-4">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                <th className="text-left pb-1">Who</th>
                {buckets.map((b) => <th key={b} className="px-1 pb-1 text-center font-semibold">{b}</th>)}
                <th className="pb-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody data-testid="photo-usage-by-person">
              {u.by_person.map((r) => <Row key={r.key} label={r.key} counts={r} total={r.total} />)}
            </tbody>
          </table>

          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
            <Boxes className="w-3 h-3" />By part type
          </div>
          <table className="w-full text-[12px] mb-4">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                <th className="text-left pb-1">Part type</th>
                {buckets.map((b) => <th key={b} className="px-1 pb-1 text-center font-semibold">{b}</th>)}
                <th className="pb-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody data-testid="photo-usage-by-part-type">
              {u.by_part_type.map((r) => <Row key={r.key} label={r.key} counts={r} total={r.total} />)}
            </tbody>
          </table>

          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
            <MessageSquare className="w-3 h-3" />Why they picked more than {u.default_photos}
          </div>
          {u.over_default === 0 ? (
            <div data-testid="photo-usage-no-reasons" className="text-xs text-slate-400">Nobody has sent more than {u.default_photos} photos yet.</div>
          ) : (
            <div data-testid="photo-usage-by-reason" className="space-y-1.5">
              {u.by_reason.map((r) => (
                <div key={r.reason} className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="font-semibold text-slate-700">{r.reason}</span>
                    <span className="font-mono text-slate-500">{r.count} read{r.count === 1 ? "" : "s"} · {r.photos} photos</span>
                  </div>
                  {(r.notes || []).length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-0.5">“{r.notes.join("”, “")}”</div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-400 mt-3">{u.accuracy_note}</div>
        </>
      )}
    </div>
  );
}
